import { isSemanticModelGeneralization, isSemanticModelRelationship } from "@dataspecer/core-v2/semantic-model/concepts";
import { DefaultGraphConversionActionConfiguration, } from "./configurations/graph-conversion-action.js";
import { ConfigurationFactory, SPECIFIC_ALGORITHM_CONVERSIONS_MAP } from "./configurations/configuration-factories.js";
import { ReactflowDimensionsEstimator } from "./dimension-estimators/reactflow-dimension-estimator.js";
import { EdgeCrossingMetric } from "./graph/graph-metrics/implemented-metrics/edge-crossing.js";
import { EdgeNodeCrossingMetric } from "./graph/graph-metrics/implemented-metrics/edge-node-crossing.js";
import { Direction } from "./util/utils.js";
export { Direction };
export { ReactflowDimensionsEstimator };
export { ReactflowDimensionsConstantEstimator } from "./dimension-estimators/constant-dimension-estimator.js";
import { placeCoordinateOnGrid, placePositionOnGrid } from "./util/utils.js";
import { GraphFactory } from "./graph/representation/graph-factory.js";
import { ALGORITHM_NAME_TO_LAYOUT_MAPPING } from "./layout-algorithms/list-of-layout-algorithms.js";
import _ from "lodash";
import { DefaultAlgorithmConfiguration } from "./configurations/algorithm-configurations.js";
import { isSemanticModelRelationshipProfile } from "@dataspecer/core-v2/semantic-model/profile/concepts";
export { AnchorOverrideSetting } from "./explicit-anchors.js";
export { placeCoordinateOnGrid, placePositionOnGrid };
export { getDefaultUserGivenAlgorithmConfigurationsFull, isUserGivenAlgorithmConfigurationStressProfile, } from "./configurations/user-algorithm-configurations.js";
export * from "./layout-configuration-model.js";
/**
 * Layout given visual entities.
 * @param visualModel The visual model the entities are part ofperform layout on.
 * @param semanticModels map of the semantic models
 * @param entitiesToLayout the entities to be layouted
 * @param config the layout configuration
 * @param nodeDimensionQueryHandler optional parameter to estimate dimensions of nodes for the layouting
 * @param explicitAnchors If this is undefined then use the anchors of visual model, otherwise it depends on the given anchors' settings.
 * @returns Promise with new positions of the visual entities.
 */
export async function performLayout(visualModel, semanticModels, entitiesToLayout, config, nodeDimensionQueryHandler, explicitAnchors) {
    const visualEntitiesPromise = performLayoutInternal(visualModel, semanticModels, entitiesToLayout, config, nodeDimensionQueryHandler, explicitAnchors);
    return visualEntitiesPromise;
}
// TODO: Maybe just return VisualEntities - so without the information about entity being an outsider
//                  On one side, I have the info in nice format so why shouldn't I give it out
//                  On other side, the caller should know about the outsiders, and the API - especially here should be probably minimal
/**
 * Layout given visual model.
 * @param visualModel The visual model to perform layout on.
 * @param semanticModels
 * @param config
 * @param nodeDimensionQueryHandler
 * @param explicitAnchors If this is undefined then use the anchors of visual model, otherwise it depends on the given anchors' settings.
 * @returns Promise with new positions of the visual entities.
 */
export async function performLayoutOfVisualModel(visualModel, semanticModels, config, nodeDimensionQueryHandler, explicitAnchors) {
    const entitiesToLayout = {
        visualEntities: [...visualModel.visualModel.getVisualEntities().keys()],
        outsiders: visualModel.outsiders
    };
    const visualEntitiesPromise = performLayoutInternal(visualModel.visualModel, semanticModels, entitiesToLayout, config, nodeDimensionQueryHandler, explicitAnchors);
    return visualEntitiesPromise;
}
/**
 * Layout given semantic model.
 */
export async function performLayoutOfSemanticModel(inputSemanticModel, semanticModelId, config, nodeDimensionQueryHandler) {
    // If we want to layout more than one, then just change the arguments and fill the semanticModels variable in loop.
    const entityModelUsedForConversion = {
        getEntities: function () {
            return inputSemanticModel;
        },
        subscribeToChanges: function (callback) {
            throw new Error("Function not implemented.");
        },
        getId: function () {
            throw new Error("Function not implemented.");
        },
        getAlias: function () {
            throw new Error("Function not implemented.");
        },
        setAlias: function (alias) {
            throw new Error("Function not implemented.");
        }
    };
    const semanticModels = new Map();
    semanticModels.set(semanticModelId, entityModelUsedForConversion);
    const semanticModelEntities = [...semanticModels.values()][0].getEntities();
    const outsiders = {};
    Object.keys(semanticModelEntities)
        .filter(entity => !isPossibleEdge(semanticModelEntities[entity]))
        .forEach(identifier => {
        outsiders[identifier] = null;
    });
    const entitiesToLayout = {
        visualEntities: [],
        outsiders,
    };
    const visualEntitiesPromise = performLayoutInternal(null, semanticModels, entitiesToLayout, config, nodeDimensionQueryHandler);
    return visualEntitiesPromise;
}
const isPossibleEdge = (entity) => {
    return isSemanticModelRelationship(entity) ||
        isSemanticModelRelationshipProfile(entity) ||
        isSemanticModelGeneralization(entity);
};
function performLayoutInternal(visualModel, semanticModels, entitiesToLayout, config, nodeDimensionQueryHandler, explicitAnchors) {
    const graph = GraphFactory.createMainGraph(null, semanticModels, visualModel, entitiesToLayout, nodeDimensionQueryHandler, explicitAnchors);
    const visualEntitiesPromise = performLayoutFromGraph(graph, config).then(async (resultAggregations) => {
        const resultingGraph = await getBestLayoutFromMetricResultAggregation(resultAggregations);
        return resultingGraph.convertWholeGraphToDataspecerRepresentation();
    });
    if (visualEntitiesPromise == undefined) {
        console.error("LAYOUT FAILED");
        throw new Error("Layout Failed");
    }
    return visualEntitiesPromise;
}
/**
 * Layout given graph based on given layout configuration
 */
export async function performLayoutFromGraph(graph, config) {
    const configurations = ConfigurationFactory.createConfigurationsContainer(config);
    const resultingAggregationsPromise = performLayoutingBasedOnConfigurations(graph, configurations);
    return resultingAggregationsPromise;
}
/**
 * Performs all relevant layout operations based on given configurations
 */
const performLayoutingBasedOnConfigurations = async (graph, configurations) => {
    let workGraph = graph;
    for (const action of configurations.layoutActionsIteratorBefore) {
        if (action.shouldCreateNewGraph) {
            workGraph = _.cloneDeep(workGraph);
        }
        if (action instanceof DefaultGraphConversionActionConfiguration) {
            SPECIFIC_ALGORITHM_CONVERSIONS_MAP[action.actionName](action, workGraph);
        }
        else if (action instanceof DefaultAlgorithmConfiguration) {
            const layoutAlgorithm = ALGORITHM_NAME_TO_LAYOUT_MAPPING[action.algorithmName];
            if (action.algorithmPhasesToCall === "ONLY-PREPARE" || action.algorithmPhasesToCall === "PREPARE-AND-RUN") {
                layoutAlgorithm.prepareFromGraph(workGraph, configurations);
            }
            if (action.algorithmPhasesToCall === "ONLY-RUN" || action.algorithmPhasesToCall === "PREPARE-AND-RUN") {
                if (action.affectedNodes === "GENERALIZATION") {
                    workGraph = await layoutAlgorithm.runGeneralizationLayout();
                }
                else {
                    workGraph = await layoutAlgorithm.run();
                }
            }
        }
    }
    return runMainLayoutAlgorithm(workGraph, configurations).then(aggregationResult => {
        return aggregationResult;
    });
};
/**
 * Run the main layouting algorithm for the given graph. The algorithm is ran multiple times based on settings.
 * The algorithm is set of steps, it isn't just call of one method, the set of steps depends on the implementation of algorithm.
 * The steps can be for example - run stress algorithm, after that run node overlap removal algorithm.
 */
const runMainLayoutAlgorithm = async (graph, configurations) => {
    const metricsWithWeights = [
        {
            name: "EdgeCrossingMetric",
            metric: new EdgeCrossingMetric(),
            weight: 1
        },
        // {
        // 	name: "EdgeCrossingAngleMetric",
        // 	metric: new EdgeCrossingAngleMetric(),
        // 	weight: 0
        // },
        {
            name: "EdgeNodeCrossingMetric",
            metric: new EdgeNodeCrossingMetric(),
            weight: 20
        },
        // {
        // 	name: "AreaMetric",
        // 	metric: new AreaMetric(),
        // 	weight: 0
        // },
        // {
        // 	name: "NodeOrthogonalityMetric",
        // 	metric: new NodeOrthogonalityMetric(),
        // 	weight: 0.0
        // },
    ];
    const computedMetricsData = createObjectsToHoldMetricsData(metricsWithWeights);
    const numberOfAlgorithmRuns = configurations.numberOfAlgorithmRuns;
    for (let i = 0; i < numberOfAlgorithmRuns; i++) {
        let workGraph = graph;
        let layoutedGraphPromise;
        for (const action of configurations.layoutActionsIterator) {
            if (action.shouldCreateNewGraph) {
                workGraph = _.cloneDeep(workGraph);
            }
            if (action instanceof DefaultGraphConversionActionConfiguration) {
                layoutedGraphPromise = SPECIFIC_ALGORITHM_CONVERSIONS_MAP[action.actionName](action, workGraph);
                workGraph = await layoutedGraphPromise;
            }
            else if (action instanceof DefaultAlgorithmConfiguration) {
                const layoutAlgorithm = ALGORITHM_NAME_TO_LAYOUT_MAPPING[action.algorithmName];
                if (action.algorithmPhasesToCall === "ONLY-PREPARE" || action.algorithmPhasesToCall === "PREPARE-AND-RUN") {
                    layoutAlgorithm.prepareFromGraph(workGraph, configurations);
                }
                if (action.algorithmPhasesToCall === "ONLY-RUN" || action.algorithmPhasesToCall === "PREPARE-AND-RUN") {
                    if (action.affectedNodes === "ALL") {
                        layoutedGraphPromise = layoutAlgorithm.run();
                        workGraph = await layoutedGraphPromise;
                    }
                    else if (action.affectedNodes === "GENERALIZATION") {
                        layoutedGraphPromise = layoutAlgorithm.runGeneralizationLayout();
                        workGraph = await layoutedGraphPromise;
                    }
                }
            }
        }
        performMetricsComputation(metricsWithWeights, computedMetricsData.metricResults, computedMetricsData.metricResultAggregations, workGraph, layoutedGraphPromise);
        configurations.resetLayoutActionsIterator();
    }
    for (const key of Object.keys(computedMetricsData.metricResultAggregations)) {
        computedMetricsData.metricResultAggregations[key].avg.absoluteValue /= numberOfAlgorithmRuns;
        computedMetricsData.metricResultAggregations[key].avg.relativeValue /= numberOfAlgorithmRuns;
    }
    // TODO RadStr: Just for debug - I am keeping it here for future
    // console.log("Metrics aggregations result: ", computedMetricsData.metricResultAggregations);
    // console.log("Metrics all results: ", computedMetricsData.metricResults);
    // console.log(await computedMetricsData.metricResultAggregations["total"].max.graphPromise);
    return computedMetricsData.metricResultAggregations;
};
function createMetricMinDefault() {
    return {
        value: {
            absoluteValue: 100000000,
            relativeValue: 100000000
        },
        graphPromise: null
    };
}
function createMetricMaxDefault() {
    return {
        value: {
            absoluteValue: -100000000,
            relativeValue: -100000000
        },
        graphPromise: null
    };
}
function createObjectsToHoldMetricsData(metrics) {
    const metricResultAggregations = {};
    metrics
        .forEach(metric => metricResultAggregations[metric.name] = {
        avg: { absoluteValue: 0, relativeValue: 0 },
        min: createMetricMinDefault(),
        max: createMetricMaxDefault(),
    });
    metricResultAggregations["total"] = {
        avg: { absoluteValue: 0, relativeValue: 0 },
        min: createMetricMinDefault(),
        max: createMetricMaxDefault(),
    };
    const metricResults = {};
    metrics.forEach(metric => metricResults[metric.name] = []);
    metricResults["total"] = [];
    return {
        metricResultAggregations,
        metricResults
    };
}
export function getBestLayoutFromMetricResultAggregation(metricResultAggregations) {
    // TODO Hard to solve by myself - Radstr: If we want to use relative metric - use max instead of min
    const resultingGraph = metricResultAggregations["total"].min.graphPromise;
    return resultingGraph;
}
export function getBestMetricResultAggregation(metricResultAggregations) {
    // TODO Hard to solve by myself - Radstr: If we want to use relative metric - use max instead of min
    const best = metricResultAggregations["total"].min;
    return best;
}
function performMetricsComputation(metricsToCompute, computedMetricsFromPreviousIterations, metricResultsAggregation, graph, layoutedGraphPromise) {
    const computedMetrics = [];
    for (const metricToCompute of metricsToCompute) {
        const computedMetric = metricToCompute.metric.computeMetric(graph);
        computedMetricsFromPreviousIterations[metricToCompute.name].push(computedMetric);
        computedMetrics.push(computedMetric);
        setMetricResultsAggregation(metricResultsAggregation, metricToCompute.name, computedMetric, layoutedGraphPromise);
    }
    const total = {
        absoluteValue: 0,
        relativeValue: 0
    };
    for (let i = 0; i < computedMetrics.length; i++) {
        total.absoluteValue += metricsToCompute[i].weight * computedMetrics[i].absoluteValue;
        total.relativeValue += metricsToCompute[i].weight * computedMetrics[i].relativeValue;
    }
    computedMetricsFromPreviousIterations["total"].push(total);
    setMetricResultsAggregation(metricResultsAggregation, "total", total, layoutedGraphPromise);
}
function setMetricResultsAggregation(metricResultsAggregation, key, computedMetric, layoutedGraphPromise) {
    metricResultsAggregation[key].avg.absoluteValue += computedMetric.absoluteValue;
    metricResultsAggregation[key].avg.relativeValue += computedMetric.relativeValue;
    // TODO Hard to solve by myself - Radstr: use relative values for metrics - possible future improvement
    //       Ideally we would work with the relativeValue (that is values in range [0, 1]),
    //       but to find the right normalization and weights for that is highly non-trivial task
    if (metricResultsAggregation[key].min.value.absoluteValue > computedMetric.absoluteValue) {
        metricResultsAggregation[key].min = {
            value: computedMetric,
            graphPromise: layoutedGraphPromise
        };
    }
    // TODO Hard to solve by myself - Radstr: again relative metric
    if (metricResultsAggregation[key].max.value.absoluteValue < computedMetric.absoluteValue) {
        metricResultsAggregation[key].max = {
            value: computedMetric,
            graphPromise: layoutedGraphPromise
        };
    }
}
//# sourceMappingURL=index.js.map